// API Client for Qwanyx - replaces direct MongoDB calls

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ApiClient {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'API request failed');
      }

      return data.data;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // GET requests
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // POST requests
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: data,
    });
  }

  // PUT requests
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data,
    });
  }

  // DELETE requests
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Health check
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/health`);
      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      return { status: 'unhealthy', error: error.message };
    }
  }
}

// Create singleton instance
const apiClient = new ApiClient();

// Qwanyx-specific API functions that mirror the original Memory.js functions
export async function getChildrenItems(parentId) {
  return apiClient.get(`/api/qwanyx/children/${parentId}`);
}

export async function recallChildren(parentId) {
  return apiClient.get(`/api/qwanyx/recall-children/${parentId}`);
}

export async function persist(delta, qwantumId, di, ed, backup = false) {
  return apiClient.post('/api/qwanyx/persist', {
    delta,
    qwantumId,
    di,
    ed,
    backup
  });
}

export async function sync(qwantumId) {
  return apiClient.get(`/api/qwanyx/sync/${qwantumId}`);
}

export async function deleteQwanta(qwantumId) {
  return apiClient.delete(`/api/qwanyx/delete/${qwantumId}`);
}

export async function recursiveDelete(qwantumId) {
  return apiClient.delete(`/api/qwanyx/recursive-delete/${qwantumId}`);
}

export async function getBatch(parentDi, startId = null, limit = 50) {
  const params = new URLSearchParams();
  if (startId) params.append('startId', startId);
  params.append('limit', limit.toString());
  
  return apiClient.get(`/api/qwanyx/batch/${parentDi}?${params}`);
}

export async function getPreviousBatch(parentDi, startId, limit = 50) {
  const params = new URLSearchParams();
  params.append('startId', startId);
  params.append('limit', limit.toString());
  
  return apiClient.get(`/api/qwanyx/previous-batch/${parentDi}?${params}`);
}

export async function getTotalCount(parentDi) {
  const result = await apiClient.get(`/api/qwanyx/count/${parentDi}`);
  return result.count;
}

export async function getQwantum(_id, qwantumId) {
  return apiClient.get(`/api/qwanyx/qwantum/${_id}/${qwantumId}`);
}

export async function fetchQwantas(query, projection) {
  return apiClient.post('/api/qwanyx/fetch', { query, projection });
}

export async function getInstancesBatch(query, skip = 0, limit = 300, projection, sortArray) {
  return apiClient.post('/api/qwanyx/batch-sorted', { query, skip, limit, projection, sortArray });
}

export async function setDocumentsBatch(records, maxRecords = 1000000) {
  return apiClient.post('/api/qwanyx/batch-insert', { records, maxRecords });
}

// ================= ADVANCED SEARCH API FUNCTIONS =================

// Search with hierarchical results (tree view)
export async function searchTree(rootId = null, di = null, query = '', fields = [], fuzzy = false, facets = [], sortBy = 'title', sortOrder = 1) {
  return apiClient.post('/api/qwanyx/search-tree', {
    rootId,
    di,
    query,
    fields,
    fuzzy,
    facets,
    sortBy,
    sortOrder
  });
}

// Search with flat results (list view with pagination)
export async function searchFlat(rootId = null, di = null, query = '', fields = [], fuzzy = false, facets = [], skip = 0, limit = 50, sortBy = 'title', sortOrder = 1, projection = null) {
  return apiClient.post('/api/qwanyx/search-flat', {
    rootId,
    di,
    query,
    fields,
    fuzzy,
    facets,
    skip,
    limit,
    sortBy,
    sortOrder,
    projection
  });
}

// Search with hybrid results (categories + flat items)
export async function searchHybrid(rootId = null, di = null, query = '', fields = [], fuzzy = false, facets = [], sortBy = 'title', sortOrder = 1) {
  return apiClient.post('/api/qwanyx/search-hybrid', {
    rootId,
    di,
    query,
    fields,
    fuzzy,
    facets,
    sortBy,
    sortOrder
  });
}

// Get available facets for a query
export async function getFacets(rootId = null, di = null, query = '', fields = [], facetFields = []) {
  return apiClient.post('/api/qwanyx/facets', {
    rootId,
    di,
    query,
    fields,
    facetFields
  });
}

// Get auto-completion suggestions
export async function getSuggestions(rootId = null, di = null, partial = '', field = 'title', limit = 10) {
  return apiClient.post('/api/qwanyx/suggest', {
    rootId,
    di,
    partial,
    field,
    limit
  });
}

// Enhanced getChildrenItems with optional domain filtering
export async function getChildrenItemsWithDomain(parentId, di = null) {
  const params = new URLSearchParams();
  if (di) params.append('di', di);
  
  const endpoint = `/api/qwanyx/children/${parentId}${params.toString() ? '?' + params.toString() : ''}`;
  return apiClient.get(endpoint);
}

// Utility function for health checks
export async function checkApiHealth() {
  return apiClient.healthCheck();
}

// User Authentication Functions
export async function loginUser(email, password) {
  return apiClient.post('/api/users/login', { email, password });
}

export async function loginUserByUrl(email, password) {
  return apiClient.get(`/api/users/login/${encodeURIComponent(email)}/${encodeURIComponent(password)}`);
}

// Export the client instance for custom requests
export { apiClient };

// For backward compatibility, provide a collection-like interface
export function qwanyxCollection() {
  console.warn('qwanyxCollection() is deprecated. Use API functions directly.');
  return {
    find: () => { throw new Error('Use getChildrenItems() or recallChildren() instead'); },
    findOne: () => { throw new Error('Use sync() instead'); },
    updateOne: () => { throw new Error('Use persist() instead'); },
    deleteOne: () => { throw new Error('Use deleteQwanta() instead'); },
  };
}